import { motion } from 'framer-motion';
import {
  CalendarIcon,
  ShareIcon,
  MapPinIcon,
  BookmarkIcon,
  ArrowTopRightOnSquareIcon,
  XMarkIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import {
  slugify,
  generateRecurringEventICS,
  getEventCalendarSummary,
} from '@/lib/util';
import Button from '@/components/Button';

export default function EventActionButtons({ event, className = '' }) {
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showTimeSlotModal, setShowTimeSlotModal] = useState(false);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState([]);

  // Safety check for event data
  if (!event) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className={`bg-white rounded p-6 ${className}`}
      >
        <h3 className="text-lg font-semibold text-brown-200 mb-4">
          Event Actions
        </h3>
        <p className="text-sm text-brown-100">
          Event information not available.
        </p>
      </motion.div>
    );
  }

  const {
    title,
    datestart,
    timestart,
    location,
    meetingpoint,
    parkfullname,
    latitude,
    longitude,
    isrecurring,
    recurrencedatestart,
    recurrencedateend,
    times,
  } = event;

  // Get calendar summary for this event
  const calendarSummary = getEventCalendarSummary(event);

  // Check if this event has multiple time slots
  const hasMultipleTimeSlots =
    times && Array.isArray(times) && times.length > 1;

  // Initialize selected time slots when modal opens
  const openTimeSlotModal = () => {
    if (hasMultipleTimeSlots) {
      // Default to selecting all time slots
      setSelectedTimeSlots(times.map((_, index) => index));
    }
    setShowTimeSlotModal(true);
  };

  // Toggle time slot selection
  const toggleTimeSlot = (index) => {
    setSelectedTimeSlots((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  // Select all time slots
  const selectAllTimeSlots = () => {
    setSelectedTimeSlots(times.map((_, index) => index));
  };

  // Deselect all time slots
  const deselectAllTimeSlots = () => {
    setSelectedTimeSlots([]);
  };

  // Generate calendar event data
  const generateCalendarEvent = (selectedSlots = null) => {
    // Create a modified event object with only selected time slots
    const eventToProcess = selectedSlots
      ? {
          ...event,
          times: selectedSlots.map((index) => times[index]),
        }
      : event;

    const icsContent = generateRecurringEventICS(eventToProcess);

    if (!icsContent) {
      console.error('Failed to generate calendar event');
      return;
    }

    // Download .ics file
    const blob = new Blob([icsContent], {
      type: 'text/calendar;charset=utf-8',
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${(title || 'event').replace(/[^a-z0-9]/gi, '_').toLowerCase()}.ics`;
    link.click();

    // Close modal if it was open
    setShowTimeSlotModal(false);
  };

  // Handle calendar button click
  const handleCalendarClick = () => {
    if (hasMultipleTimeSlots) {
      openTimeSlotModal();
    } else {
      generateCalendarEvent();
    }
  };

  // Share event
  const shareEvent = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title || 'Event',
          text: `Check out this event: ${title || 'Event'}`,
          url: typeof window !== 'undefined' ? window.location.href : '',
        });
      } catch (error) {
        // console.log('Error sharing:', error);
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(
          typeof window !== 'undefined' ? window.location.href : ''
        );
        setShowShareMenu(false);
        // Show success message (you could add a toast notification here)
      } catch (error) {
        // console.log('Error copying to clipboard:', error);
      }
    }
  };

  // Get directions
  const getDirections = () => {
    if (latitude && longitude) {
      const url = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
      window.open(url, '_blank');
    } else if (location || meetingpoint || parkfullname) {
      const destination = encodeURIComponent(
        location || meetingpoint || parkfullname
      );
      const url = `https://www.google.com/maps/search/?api=1&query=${destination}`;
      window.open(url, '_blank');
    }
  };

  // Toggle bookmark
  // const toggleBookmark = () => {
  //     setIsBookmarked(!isBookmarked);
  // };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.2 }}
      className={`bg-white rounded p-6 ${className}`}
    >
      <h3 className="text-lg font-semibold text-brown-200 mb-4">
        Event Actions
      </h3>

      <div className="space-y-3">
        {/* Add to Calendar */}
        <div className="space-y-2">
          <Button
            onClick={handleCalendarClick}
            variant="primary"
            size="lg"
            fullWidth
            icon={<CalendarIcon className="w-5 h-5 -mt-1" />}
            iconPosition="left"
          >
            {isrecurring
              ? 'Add Recurring Event to Calendar'
              : 'Add to Calendar'}
          </Button>

          {/* Show info for recurring events */}
          {calendarSummary && calendarSummary.type === 'recurring' && (
            <p className="text-xs text-brown-100 text-center">
              {hasMultipleTimeSlots
                ? `Click to select which time slots to add (${times.length} available)`
                : calendarSummary.summary}
            </p>
          )}
        </div>

        {/* Get Directions */}
        {(latitude ||
          longitude ||
          location ||
          meetingpoint ||
          parkfullname) && (
          <Button
            onClick={getDirections}
            variant="secondary"
            size="lg"
            fullWidth
            icon={<MapPinIcon className="w-5 h-5 -mt-1" />}
            iconPosition="left"
          >
            Get Directions
          </Button>
        )}

        {/* Share Event */}
        <div className="relative">
          <Button
            onClick={() => setShowShareMenu(!showShareMenu)}
            variant="purple"
            size="lg"
            fullWidth
            icon={<ShareIcon className="w-5 h-5" />}
            iconPosition="left"
          >
            Share Event
          </Button>

          {showShareMenu && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-beige-100 rounded-lg shadow-lg p-2 z-10"
            >
              <button
                onClick={shareEvent}
                className="w-full text-left px-3 py-2 text-sm text-brown-200 hover:bg-beige-50 rounded transition-colors"
              >
                {navigator.share ? 'Share via native app' : 'Copy link'}
              </button>
            </motion.div>
          )}
        </div>

        {/* Bookmark (Placeholder for future user accounts) */}
        {/* <Button
                    onClick={toggleBookmark}
                    variant={isBookmarked ? "secondary" : "outline"}
                    size="lg"
                    fullWidth
                    icon={<BookmarkIcon className={`w-5 h-5 ${isBookmarked ? 'fill-current' : ''}`} />}
                    iconPosition="left"
                >
                    {isBookmarked ? 'Bookmarked' : 'Bookmark'}
                </Button> */}
      </div>

      {/* Quick Links */}
      {(event.regresUrl ||
        event.regresurl ||
        event.infoUrl ||
        event.infourl ||
        event.parkFullName ||
        event.parkfullname) && (
        <div className="mt-6 pt-4 border-t border-beige-100 space-y-3">
          <h4 className="text-sm font-medium text-brown-200 mb-3">
            Quick Links
          </h4>
          <div className="space-y-2">
            {(event.regresUrl || event.regresurl) && (
              <a
                href={event.regresUrl || event.regresurl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 text-sm text-green-100 hover:text-green-100/80 transition-colors"
              >
                <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                Register for Event
              </a>
            )}
            {(event.infoUrl || event.infourl) && (
              <a
                href={event.infoUrl || event.infourl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 text-sm text-green-100 hover:text-green-100/80 transition-colors"
              >
                Learn More
                <ArrowTopRightOnSquareIcon className="w-3 h-3" />
              </a>
            )}
            {(event.parkFullName || event.parkfullname) && (
              <a
                href={`/park/${slugify(event.parkFullName || event.parkfullname)}`}
                className="flex items-center gap-2 text-xs text-green-100 hover:text-green-100/80 transition-colors"
              >
                View Park Details
                <ArrowTopRightOnSquareIcon className="w-3 h-3 -mt-1" />
              </a>
            )}
          </div>
        </div>
      )}

      {/* Time Slot Selection Modal */}
      {showTimeSlotModal && hasMultipleTimeSlots && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowTimeSlotModal(false)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-brown-200">
                Select Time Slots
              </h3>
              <button
                onClick={() => setShowTimeSlotModal(false)}
                className="hover:text-brown-200"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm mb-4">
              Choose which time slots you&apos;d like to add to your calendar:
            </p>

            {/* Select All/None Buttons */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={selectAllTimeSlots}
                className="text-xs px-3 py-1 pt-1.5 bg-green-100 text-white rounded hover:bg-green-100/80"
              >
                Select All
              </button>
              <button
                onClick={deselectAllTimeSlots}
                className="text-xs px-3 py-1 pt-1.5 bg-black/10 text-black rounded hover:bg-black/20"
              >
                Deselect All
              </button>
            </div>

            {/* Time Slot List */}
            <div className="space-y-2 mb-6">
              {times.map((timeSlot, index) => (
                <label
                  key={index}
                  className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedTimeSlots.includes(index)
                      ? 'border-green-100 bg-green-100/10'
                      : 'border-brown-100 hover:border-brown-200'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedTimeSlots.includes(index)}
                    onChange={() => toggleTimeSlot(index)}
                    className="sr-only"
                  />
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center mr-3 ${
                      selectedTimeSlots.includes(index)
                        ? 'border-green-100 bg-green-100'
                        : 'border-brown-200'
                    }`}
                  >
                    {selectedTimeSlots.includes(index) && (
                      <CheckIcon className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-brown-200">
                      {timeSlot.timestart} - {timeSlot.timeend}
                    </p>
                    <p className="text-xs text-brown-100">
                      {calendarSummary?.totalEvents / times.length} occurrences
                    </p>
                  </div>
                </label>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={() => setShowTimeSlotModal(false)}
                variant="outline"
                size="md"
                fullWidth
                className="text-brown-200 border-brown-200"
              >
                Cancel
              </Button>
              <Button
                onClick={() => generateCalendarEvent(selectedTimeSlots)}
                variant="primary"
                size="md"
                fullWidth
                disabled={selectedTimeSlots.length === 0}
              >
                Add {selectedTimeSlots.length} Time Slot
                {selectedTimeSlots.length !== 1 ? 's' : ''} to Calendar
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
}
